// Runtime settings (010 T038; FR-044, research A-24): the staff app reads /admin/config.json before its first render,
// so one build runs in every environment (revision 5: its name for the footer). A missing file or field hides the link it enables; an invalid file is
// reported and treated as empty. The file holds public values only, never secrets.
import { createContext, useContext } from 'react';

/** A labelled link to a local development tool. */
export interface DevLink {
  label: string;
  url: string;
}

/** The settings the staff app understands. */
export interface RuntimeSettings {
  /** The staff guide ("Need help? View the guide"); the help card is hidden without it. */
  helpUrl?: string;
  /** The developer documentation site; its link in the user menu is hidden without it. */
  docsUrl?: string;
  /**
   * The internal documentation (revision 28, FR-116; revision 29: `/internal/` on the docs site, no sign-in); the user
   * menu's "Internal documentation" links to it and is hidden without it.
   */
  internalDocsUrl?: string;
  /** The Daily Dose web app's base address, for "View in user app" links; hidden without it. */
  readerAppBase?: string;
  /** The environment's name shown in the footer ("Staging"; revision 5); none when absent. */
  environment?: string;
  /** Local message catchers linked from the App demo's Demo inbox (revision 5, research A-34); absent on staging. */
  devLinks?: DevLink[];
  /** Why the file could not be used, when it could not. */
  problem?: string;
}

/**
 * Reads a web address: an absolute http(s) URL, or (revision 42) a same-site path such as `/app`, resolved against the
 * address the app was opened on, so one environment works on each of its names.
 * @param value anything
 * @returns the address without a trailing slash, or `undefined` when it is neither
 */
function webAddress(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const text = value.trim();
  // A protocol-relative address ("//host") names another site, never this one.
  if (text.startsWith('//')) return undefined;
  try {
    const url = text.startsWith('/') ? new URL(text, window.location.origin) : new URL(text);
    return url.protocol.startsWith('http') ? url.href.replace(/\/$/, '') : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads a short label.
 * @param value anything
 * @returns the trimmed text of at most 40 characters, or `undefined`
 */
function shortLabel(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text && text.length <= 40 ? text : undefined;
}

/**
 * Reads the development links: at most four, each with a short label and a web address.
 * @param value anything
 * @returns the links, or `undefined` when there are none
 */
function devLinks(value: unknown): DevLink[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const links = value
    .map((v) => {
      const o = v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
      const label = shortLabel(o.label);
      const url = webAddress(o.url);
      return label && url ? { label, url } : null;
    })
    .filter((l): l is DevLink => l !== null)
    .slice(0, 4);
  return links.length ? links : undefined;
}

/**
 * Reads the settings from a parsed config.json.
 * @param value the parsed file (anything)
 * @returns the settings, with missing or invalid fields left out
 */
export function parseRuntimeSettings(value: unknown): RuntimeSettings {
  const o =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return {
    helpUrl: webAddress(o.helpUrl),
    docsUrl: webAddress(o.docsUrl),
    internalDocsUrl: webAddress(o.internalDocsUrl),
    readerAppBase: webAddress(o.readerAppBase),
    environment: shortLabel(o.environment),
    devLinks: devLinks(o.devLinks),
  };
}

/**
 * Fetches and parses the settings file.
 * @param url where the file is (the app's base path + config.json)
 * @param fetchFn the fetch to use
 * @returns the settings; never throws
 */
export async function loadRuntimeSettings(
  url: string,
  fetchFn: typeof fetch = fetch,
): Promise<RuntimeSettings> {
  let response: Response;
  try {
    response = await fetchFn(url, { cache: 'no-store' });
  } catch {
    return { ...parseRuntimeSettings({}), problem: 'config.json could not be loaded' };
  }
  if (!response.ok) return parseRuntimeSettings({});
  try {
    return parseRuntimeSettings(await response.json());
  } catch {
    return { ...parseRuntimeSettings({}), problem: 'config.json is not valid JSON' };
  }
}

/** The settings context; the providers fill it. */
export const SettingsContext = createContext<RuntimeSettings>(parseRuntimeSettings({}));

/**
 * The runtime settings.
 * @returns the settings from the nearest provider
 */
export function useSettings(): RuntimeSettings {
  return useContext(SettingsContext);
}
